/**
 * WebhooksTab - Admin webhooks management tab
 * Full feature parity with inline implementation
 */
import { motion } from 'framer-motion';
import { Webhook, RefreshCw, Eye, RotateCcw, CheckCircle, XCircle, AlertCircle, Zap } from 'lucide-react';
import { Button } from '../../ui/button';
import { useLanguage } from '../../../context/LanguageContext';

export function WebhooksTab({ 
  webhookEvents,
  webhookStats,
  onReprocess,
  onRefresh,
  formatDate,
  pilotMode
}) {
  const { language, t } = useLanguage();

  const getWebhookStatusColor = (status) => {
    switch (status) {
      case 'processed':
        return 'bg-emerald-50 text-emerald-700';
      case 'failed':
        return 'bg-red-50 text-red-700';
      case 'ignored':
        return 'bg-slate-100 text-slate-600';
      default:
        return 'bg-amber-50 text-amber-700';
    }
  };

  const getWebhookStatusIcon = (status) => {
    switch (status) {
      case 'processed':
        return <CheckCircle className="w-3 h-3" />;
      case 'failed':
        return <XCircle className="w-3 h-3" />;
      case 'ignored':
        return <AlertCircle className="w-3 h-3" />;
      default:
        return null;
    }
  };

  return (
    <motion.div
      key="webhooks-tab"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="space-y-4"
    >
      {/* Pilot Mode Banner */}
      {pilotMode?.enabled && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2" data-testid="webhook-pilot-banner">
          <Zap className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-medium text-amber-800">🧪 SIMULATED (PILOT MODE)</span>
          <span className="text-xs text-amber-600 ml-2">Events from mock provider are simulated</span>
        </div>
      )}

      {/* Webhook Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <span className="text-xs font-medium text-slate-500">{language === 'es' ? 'Total (24h)' : 'Total (24h)'}</span>
          <p className="text-2xl font-bold text-slate-700">{webhookStats?.total_24h || 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <span className="text-xs font-medium text-emerald-600">{language === 'es' ? 'Procesados' : 'Processed'}</span>
          <p className="text-2xl font-bold text-emerald-600">{webhookStats?.processed || 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <span className="text-xs font-medium text-red-600">{language === 'es' ? 'Fallidos' : 'Failed'}</span>
          <p className="text-2xl font-bold text-red-600">{webhookStats?.failed || 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <span className="text-xs font-medium text-slate-500">{language === 'es' ? 'Ignorados' : 'Ignored'}</span>
          <p className="text-2xl font-bold text-slate-400">{webhookStats?.ignored || 0}</p>
        </div>
      </div>

      {/* Webhooks Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="webhooks-table">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Webhook className="w-5 h-5 text-indigo-600" />
            {language === 'es' ? 'Eventos de Webhook' : 'Webhook Events'}
          </h3>
          {onRefresh && (
            <Button size="sm" variant="outline" onClick={onRefresh}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          )}
        </div>
        {webhookEvents.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Webhook className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>{language === 'es' ? 'No hay eventos de webhook todavía' : 'No webhook events yet'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{language === 'es' ? 'ID Evento' : 'Event ID'}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{language === 'es' ? 'Tipo' : 'Type'}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Provider</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{language === 'es' ? 'Estado' : 'Status'}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{language === 'es' ? 'Recibido' : 'Received'}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {webhookEvents.map((event) => (
                  <tr key={event.id} className="hover:bg-slate-50" data-testid={`webhook-${event.event_id}`}>
                    <td className="px-6 py-4 font-mono text-xs text-slate-900">{event.event_id?.slice(0, 16)}...</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{event.type}</td>
                    <td className="px-6 py-4">
                      {event.provider === 'mock' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          <Zap className="w-3 h-3" />
                          mock
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">{event.provider || 'stripe'}</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium capitalize ${getWebhookStatusColor(event.status)}`}>
                        {getWebhookStatusIcon(event.status)}
                        {event.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">{formatDate(event.received_at)}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1">
                        {event.status === 'failed' && onReprocess && (
                          <Button size="sm" variant="outline" onClick={() => onReprocess(event.event_id)} title={language === 'es' ? 'Reprocesar' : 'Reprocess'}>
                            <RotateCcw className="w-3 h-3" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" title={language === 'es' ? 'Ver detalles' : 'View details'}>
                          <Eye className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default WebhooksTab;
